/**
 * Link quality per site: packet loss, round-trip time and jitter.
 *
 * These are the numbers behind "the connection feels bad" when every light is
 * still green — a site can be `connected`, at 2% loss, and unusable for voice.
 * Thresholds live in `status.ts` so a tone here means the same thing it means
 * everywhere else on the board.
 */

import { Badge, DataGrid, EmptyState } from "@norican/react";

import type { NetworkSite } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatMs, formatPct, jitterTone, lossTone, rttTone } from "./status";

interface LinkQualityProps {
  /** Sites carrying `metrics` from the `accountMetrics` query. */
  sites: NetworkSite[];
  /** Length of the metrics window, in seconds. */
  windowSeconds: number | null | undefined;
}

export function LinkQuality({ sites, windowSeconds }: LinkQualityProps) {
  const measured = sites.filter((site) => site.metrics != null);

  return (
    <PanelCard
      title="Link quality"
      caption={windowSeconds ? `Last ${Math.round(windowSeconds / 60)} min` : undefined}
      padding="none"
    >
      {measured.length === 0 ? (
        <div className="p-6">
          <EmptyState
            size="sm"
            title="No quality measurements"
            description="Loss, latency and jitter come from Cato's accountMetrics query, which returned nothing for this window."
          />
        </div>
      ) : (
        <div className="h-[28rem]">
          {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
              sticky header only get a scroll region inside a DEFINITE height.
              Without one they latch onto the page viewport and drift while the
              page scrolls. Every DataGrid story wraps the grid this way. */}
          <DataGrid<NetworkSite>
            aria-label="Link quality per site"
            data={measured}
            getRowId={(site) => site.id}
            density="compact"
            columns={[
              {
                id: "name",
                header: "Site",
                accessor: (site) => site.name,
                sortable: true,
                pinned: "left",
                cell: (site) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{site.name}</span>
                    <span className="text-fg-muted text-xs">{site.pop_name ?? "—"}</span>
                  </div>
                ),
              },
              {
                id: "loss-down",
                header: "Loss ↓",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.lost_downstream_pct ?? null,
                sortable: true,
                cell: (site) => (
                  <Badge
                    tone={lossTone(site.metrics?.lost_downstream_pct)}
                    variant="subtle"
                    size="sm"
                  >
                    {formatPct(site.metrics?.lost_downstream_pct)}
                  </Badge>
                ),
              },
              {
                id: "loss-up",
                header: "Loss ↑",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.lost_upstream_pct ?? null,
                sortable: true,
                cell: (site) => (
                  <Badge
                    tone={lossTone(site.metrics?.lost_upstream_pct)}
                    variant="subtle"
                    size="sm"
                  >
                    {formatPct(site.metrics?.lost_upstream_pct)}
                  </Badge>
                ),
              },
              {
                id: "rtt",
                header: "Latency (RTT)",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.rtt ?? null,
                sortable: true,
                cell: (site) => (
                  <Badge tone={rttTone(site.metrics?.rtt)} variant="subtle" size="sm">
                    {formatMs(site.metrics?.rtt)}
                  </Badge>
                ),
              },
              {
                id: "jitter-down",
                header: "Jitter ↓",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.jitter_downstream ?? null,
                sortable: true,
                cell: (site) => (
                  <span className="text-fg tabular-nums">
                    {formatMs(site.metrics?.jitter_downstream)}
                  </span>
                ),
              },
              {
                id: "jitter-up",
                header: "Jitter ↑",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.jitter_upstream ?? null,
                sortable: true,
                cell: (site) => (
                  <Badge
                    tone={jitterTone(site.metrics?.jitter_upstream)}
                    variant="subtle"
                    size="sm"
                  >
                    {formatMs(site.metrics?.jitter_upstream)}
                  </Badge>
                ),
              },
              {
                id: "hosts",
                header: "Hosts",
                align: "end",
                numeric: true,
                accessor: (site) => site.metrics?.host_count ?? null,
                sortable: true,
              },
            ]}
          />
        </div>
      )}
    </PanelCard>
  );
}
